import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Pagination,
  Chip,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Snackbar,
  Paper,
  Stack,
  Tabs,
  Tab,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material'
import {
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Warning as WarningIcon,
  HelpOutline as HelpOutlineIcon,
  Quiz as QuizIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { TestService } from '../services/testService'
import { QuestionService } from '../services/questionService'
import { complianceService } from '../services/complianceService'
import { QuestionReportService } from '../services/questionReportService'
import { QuestionReport, QuestionType } from '../types/questionReport'
import { QuestionEditDialog } from '../components/test/QuestionEditDialog'
import { TestCreateDialog } from '../components/test/TestCreateDialog'
import { TestGenerationModal } from '../components/test/TestGenerationModal'
import { QuestionInput, QuestionWithAnswer } from '../types/question'
import { SearchField } from '../components/common/SearchField'
import { useSearchParams } from 'react-router-dom'

interface TestSummary {
  id: string
  title: string
  description?: string
  passingScore: number
  timeLimit?: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  complianceRule: {
    id: string
    title: string
    category: string
  }
  questionCount: number
  resultCount: number
}

export const TestManagePage: React.FC = () => {
  const { t, i18n } = useTranslation('test')
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tests, setTests] = useState<TestSummary[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')

  // テスト詳細モーダル
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [selectedTest, setSelectedTest] = useState<any>(null)

  // テスト作成ダイアログ
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  // AIテスト生成モーダル
  const [aiGenerationOpen, setAiGenerationOpen] = useState(false)

  // 設問編集ダイアログ
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState<QuestionWithAnswer | null>(null)

  // 設問削除確認ダイアログ
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingQuestion, setDeletingQuestion] = useState<QuestionWithAnswer | null>(null)

  // テスト削除確認ダイアログ
  const [deleteTestDialogOpen, setDeleteTestDialogOpen] = useState(false)
  const [deletingTest, setDeletingTest] = useState<TestSummary | null>(null)

  // タブ（URLパラメータから初期値を取得）
  const [selectedTab, setSelectedTab] = useState(() => {
    const tabParam = searchParams.get('tab')
    return tabParam === 'reports' ? 1 : 0
  })

  // 設問報告関連
  const { t: tReport } = useTranslation('questionReport')
  const [reports, setReports] = useState<QuestionReport[]>([])
  const [reportsLoading, setReportsLoading] = useState(false)
  const [reportCount, setReportCount] = useState(0)
  const [reportPage, setReportPage] = useState(0)
  const [reportRowsPerPage, setReportRowsPerPage] = useState(10)
  const [reportTotal, setReportTotal] = useState(0)
  const [deleteReportDialogOpen, setDeleteReportDialogOpen] = useState(false)
  const [deletingReport, setDeletingReport] = useState<QuestionReport | null>(null)

  // 報告詳細ダイアログ
  const [reportDetailDialogOpen, setReportDetailDialogOpen] = useState(false)
  const [detailReport, setDetailReport] = useState<QuestionReport | null>(null)

  const limit = 12

  // 権限チェック
  const canEdit = user?.role === 'CORPORATE_ADMIN' || user?.role === 'SUPER_ADMIN'

  // データ読み込み
  const loadData = React.useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      const [testsResponse, categoriesResponse] = await Promise.all([
        TestService.getTests({
          page,
          limit,
          search: searchTerm || undefined,
          category: selectedCategory || undefined,
        }),
        complianceService.getCategories(),
      ])

      setTests(testsResponse.tests)
      setTotalPages(testsResponse.pagination.pages)
      setCategories(categoriesResponse.categories)
    } catch (err: any) {
      console.error('データ読み込み失敗:', err)
      setError(t('list.loadError'))
    } finally {
      setLoading(false)
    }
  }, [page, searchTerm, selectedCategory, limit, t])

  useEffect(() => {
    loadData()
  }, [loadData])

  // 報告件数を取得（タブバッジ用）
  const loadReportCount = React.useCallback(async () => {
    if (!canEdit) return
    try {
      const response = await QuestionReportService.getReportCount()
      setReportCount(response.count)
    } catch (err) {
      console.error('報告件数取得失敗:', err)
    }
  }, [canEdit])

  useEffect(() => {
    loadReportCount()
  }, [loadReportCount])

  // 報告一覧を取得
  const loadReports = React.useCallback(async () => {
    if (!canEdit) return
    setReportsLoading(true)
    try {
      const response = await QuestionReportService.getReports({
        page: reportPage + 1,
        limit: reportRowsPerPage,
      })
      setReports(response.reports)
      setReportTotal(response.pagination.total)
    } catch (err) {
      console.error('報告一覧取得失敗:', err)
      setError(tReport('list.loadError'))
    } finally {
      setReportsLoading(false)
    }
  }, [canEdit, reportPage, reportRowsPerPage, tReport])

  useEffect(() => {
    if (selectedTab === 1) {
      loadReports()
    }
  }, [selectedTab, loadReports])

  // 検索処理
  const handleSearch = () => {
    setPage(1)
    loadData()
  }

  // テスト詳細表示
  const handleViewTest = async (test: TestSummary) => {
    try {
      const testDetail = await TestService.getTest(test.id)
      // 設問をorder順にソート
      if (testDetail.questions) {
        testDetail.questions.sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
      }
      setSelectedTest(testDetail)
      setDetailDialogOpen(true)
    } catch (error) {
      console.error('テスト詳細取得エラー:', error)
      setError(t('list.detailError'))
    }
  }

  // テスト詳細を再読み込み
  const reloadTestDetail = async () => {
    if (selectedTest) {
      try {
        const testDetail = await TestService.getTest(selectedTest.id)
        if (testDetail.questions) {
          testDetail.questions.sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
        }
        setSelectedTest(testDetail)
      } catch (error) {
        console.error('テスト詳細再読み込みエラー:', error)
      }
    }
  }

  // 日付フォーマット
  const formatDate = (dateString: string) => {
    const locale = i18n.language === 'ja' ? 'ja-JP' : 'en-US'
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // テスト作成成功時
  const handleTestCreated = (test: any) => {
    setSuccessMessage(t('create.success', 'テストを作成しました'))
    loadData()
  }

  // 設問追加
  const handleAddQuestion = () => {
    setEditingQuestion(null)
    setQuestionDialogOpen(true)
  }

  // 設問編集
  const handleEditQuestion = (question: QuestionWithAnswer) => {
    setEditingQuestion(question)
    setQuestionDialogOpen(true)
  }

  // 設問保存
  const handleSaveQuestion = async (data: QuestionInput) => {
    if (!selectedTest) return

    if (editingQuestion) {
      // 更新
      const response = await QuestionService.updateQuestion(editingQuestion.id, data)
      if (response.warning) {
        setSuccessMessage(response.warning)
      } else {
        setSuccessMessage(t('question.updateSuccess', '設問を更新しました'))
      }
    } else {
      // 新規作成
      await QuestionService.createQuestion(selectedTest.id, data)
      setSuccessMessage(t('question.createSuccess', '設問を作成しました'))
    }

    await reloadTestDetail()
    loadData() // 一覧も更新
  }

  // 設問削除確認
  const handleDeleteQuestion = (question: QuestionWithAnswer) => {
    setDeletingQuestion(question)
    setDeleteDialogOpen(true)
  }

  // 設問削除実行
  const handleConfirmDelete = async () => {
    if (!deletingQuestion) return

    try {
      await QuestionService.deleteQuestion(deletingQuestion.id)
      setSuccessMessage(t('question.deleteSuccess', '設問を削除しました'))
      setDeleteDialogOpen(false)
      setDeletingQuestion(null)
      await reloadTestDetail()
      loadData()
    } catch (err: any) {
      setError(err.message || t('question.deleteError', '設問の削除に失敗しました'))
      setDeleteDialogOpen(false)
    }
  }

  // テスト削除確認
  const handleDeleteTest = (test: TestSummary) => {
    setDeletingTest(test)
    setDeleteTestDialogOpen(true)
  }

  // テスト削除実行
  const handleConfirmDeleteTest = async () => {
    if (!deletingTest) return

    try {
      const result = await TestService.deleteTest(deletingTest.id)
      if (result.deactivated) {
        setSuccessMessage(
          t('delete.deactivated', 'テストを無効化しました（受講履歴があるため削除できません）')
        )
      } else {
        setSuccessMessage(t('delete.success', 'テストを削除しました'))
      }
      setDeleteTestDialogOpen(false)
      setDeletingTest(null)
      loadData()
    } catch (err: any) {
      setError(err.message || t('delete.error', 'テストの削除に失敗しました'))
      setDeleteTestDialogOpen(false)
    }
  }

  // 設問並び替え（上に移動）
  const handleMoveUp = async (index: number) => {
    if (!selectedTest || index <= 0) return

    const questions = [...selectedTest.questions]
    const questionIds = questions.map((q: any) => q.id)

    // 順序を入れ替え
    ;[questionIds[index - 1], questionIds[index]] = [questionIds[index], questionIds[index - 1]]

    try {
      await QuestionService.reorderQuestions(selectedTest.id, questionIds)
      await reloadTestDetail()
    } catch (err: any) {
      setError(err.message || t('question.reorderError', '並び替えに失敗しました'))
    }
  }

  // 設問並び替え（下に移動）
  const handleMoveDown = async (index: number) => {
    if (!selectedTest || index >= selectedTest.questions.length - 1) return

    const questions = [...selectedTest.questions]
    const questionIds = questions.map((q: any) => q.id)

    // 順序を入れ替え
    ;[questionIds[index], questionIds[index + 1]] = [questionIds[index + 1], questionIds[index]]

    try {
      await QuestionService.reorderQuestions(selectedTest.id, questionIds)
      await reloadTestDetail()
    } catch (err: any) {
      setError(err.message || t('question.reorderError', '並び替えに失敗しました'))
    }
  }

  // 正解の表示形式を取得
  const getCorrectAnswerDisplay = (question: any) => {
    if (!question.correctAnswer) {
      return '-'
    }
    if (question.questionType === 'ESSAY') {
      return question.correctAnswer || '-'
    }
    if (question.questionType === 'TRUE_FALSE') {
      const index = parseInt(question.correctAnswer)
      return question.options?.[index] || question.correctAnswer
    }
    if (question.questionType === 'SINGLE_CHOICE') {
      const index = parseInt(question.correctAnswer)
      return question.options?.[index]
        ? `${index + 1}. ${question.options[index]}`
        : question.correctAnswer
    }
    if (question.questionType === 'MULTIPLE_CHOICE') {
      const indices = question.correctAnswer.split(',').map(Number)
      return indices
        .map((i: number) =>
          question.options?.[i] ? `${i + 1}. ${question.options[i]}` : `${i + 1}`
        )
        .join(', ')
    }
    return question.correctAnswer || '-'
  }

  // 報告削除
  const handleDeleteReport = async () => {
    if (!deletingReport) return
    try {
      await QuestionReportService.deleteReport(deletingReport.id)
      setSuccessMessage(tReport('delete.success'))
      setDeleteReportDialogOpen(false)
      setDeletingReport(null)
      loadReports()
      loadReportCount()
    } catch (err) {
      console.error('報告削除失敗:', err)
      setError(tReport('delete.error'))
    }
  }

  // 報告詳細表示
  const handleOpenReportDetail = (report: QuestionReport) => {
    setDetailReport(report)
    setReportDetailDialogOpen(true)
  }

  const handleCloseReportDetail = () => {
    setReportDetailDialogOpen(false)
    setDetailReport(null)
  }

  // 設問タイプラベル取得
  const getQuestionTypeLabel = (type: QuestionType): string => {
    return tReport(`detail.questionTypes.${type}`)
  }

  // 正解選択肢判定
  const isCorrectOption = (
    option: string,
    correctAnswer: string,
    questionType: QuestionType
  ): boolean => {
    if (questionType === 'MULTIPLE_CHOICE') {
      const correctAnswers = correctAnswer.split(',').map((a) => a.trim())
      return correctAnswers.includes(option)
    }
    return option === correctAnswer
  }

  // タブ変更
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
    // URLパラメータを更新
    if (newValue === 1) {
      setSearchParams({ tab: 'reports' })
    } else {
      setSearchParams({})
    }
  }

  // 日付フォーマット（報告用）
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString(i18n.language === 'ja' ? 'ja-JP' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('pageTitle')}
        </Typography>
        {canEdit && selectedTab === 0 && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAiGenerationOpen(true)}
          >
            {t('create.manualTest', 'テストを作成')}
          </Button>
        )}
      </Box>

      {/* タブ */}
      {canEdit && (
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab icon={<QuizIcon />} iconPosition="start" label={t('tabs.tests', 'テスト一覧')} />
            <Tab
              icon={
                <Badge badgeContent={reportCount} color="primary" max={99}>
                  <HelpOutlineIcon />
                </Badge>
              }
              iconPosition="start"
              label={tReport('tabs.reports', '設問報告')}
            />
          </Tabs>
        </Paper>
      )}

      {/* テスト一覧タブ */}
      {selectedTab === 0 && (
        <>
          {/* 検索・フィルター */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <SearchField
                value={searchTerm}
                onChange={setSearchTerm}
                onSearch={handleSearch}
                placeholder={t('list.searchPlaceholder')}
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('list.category')}</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label={t('list.category')}
                >
                  <MenuItem value="">{t('list.filter.all')}</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Paper>

          {/* エラー表示 */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* ローディング */}
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* テスト一覧 */}
              {tests.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="textSecondary">
                    {t('list.noTests')}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                      },
                      gap: 3,
                    }}
                  >
                    {tests.map((test) => (
                      <Card
                        key={test.id}
                        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                      >
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            mb={1}
                          >
                            <Typography variant="h6" component="h3" gutterBottom>
                              {test.title}
                            </Typography>
                            <Chip
                              label={test.isActive ? t('list.active') : t('list.inactive')}
                              color={test.isActive ? 'primary' : 'default'}
                              size="small"
                            />
                          </Box>

                          {test.description && (
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {test.description}
                            </Typography>
                          )}

                          <Box mb={2}>
                            <Chip
                              label={test.complianceRule.category}
                              color="primary"
                              variant="outlined"
                              size="small"
                              sx={{ mr: 1, mb: 1 }}
                            />
                          </Box>

                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {t('list.complianceRule')}: {test.complianceRule.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('list.questionCountLabel', { count: test.questionCount })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('list.passingScoreLabel', { score: test.passingScore })}
                            </Typography>
                            {test.timeLimit && (
                              <Typography variant="body2" color="text.secondary">
                                {t('list.timeLimitLabel', { minutes: test.timeLimit })}
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              {t('list.resultCount', { count: test.resultCount })}
                            </Typography>
                          </Box>

                          <Box mt={2}>
                            <Typography variant="caption" color="text.secondary">
                              {t('list.created')}: {formatDate(test.createdAt)}
                            </Typography>
                          </Box>
                        </CardContent>

                        <CardActions sx={{ justifyContent: 'space-between' }}>
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewTest(test)}
                          >
                            {t('list.viewDetail')}
                          </Button>
                          {canEdit && (
                            <Tooltip
                              title={
                                test.resultCount > 0
                                  ? t('delete.hasResultsWarning', '受講履歴があります')
                                  : ''
                              }
                            >
                              <IconButton
                                size="small"
                                sx={{ color: 'text.secondary', '&:hover': { color: '#EF4444' } }}
                                onClick={() => handleDeleteTest(test)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </CardActions>
                      </Card>
                    ))}
                  </Box>

                  {/* ページネーション */}
                  {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={4}>
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* 設問報告タブ */}
      {selectedTab === 1 && canEdit && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={3}>
            <HelpOutlineIcon sx={{ color: 'text.secondary' }} />
            <Typography variant="h6" component="h2">
              {tReport('list.title', '設問報告一覧')}
            </Typography>
          </Box>

          {reportsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : reports.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                {tReport('list.noReports', '報告はありません')}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{tReport('table.question', '設問')}</TableCell>
                      <TableCell>{tReport('table.test', 'テスト')}</TableCell>
                      <TableCell>{tReport('table.reporter', '報告者')}</TableCell>
                      <TableCell>{tReport('table.comment', 'コメント')}</TableCell>
                      <TableCell>{tReport('table.createdAt', '報告日時')}</TableCell>
                      <TableCell align="center">{tReport('table.actions', '操作')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.map((report) => (
                      <TableRow
                        key={report.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleOpenReportDetail(report)}
                      >
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography variant="body2" noWrap title={report.question?.questionText}>
                            {report.question?.questionText || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{report.test?.title || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {report.reporter?.name || report.reporter?.email || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" noWrap title={report.comment || ''}>
                            {report.comment || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(report.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={tReport('detail.title', '詳細')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleOpenReportDetail(report)
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={tReport('delete.button', '削除')}>
                            <IconButton
                              size="small"
                              sx={{ color: 'text.secondary', '&:hover': { color: '#EF4444' } }}
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeletingReport(report)
                                setDeleteReportDialogOpen(true)
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={reportTotal}
                page={reportPage}
                onPageChange={(_, newPage) => setReportPage(newPage)}
                rowsPerPage={reportRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setReportRowsPerPage(parseInt(e.target.value, 10))
                  setReportPage(0)
                }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage={tReport('pagination.rowsPerPage', '表示件数')}
                labelDisplayedRows={({ from, to, count }) =>
                  tReport('pagination.displayedRows', { from, to, count })
                }
              />
            </>
          )}
        </Paper>
      )}

      {/* 報告削除確認ダイアログ */}
      <Dialog
        open={deleteReportDialogOpen}
        onClose={() => setDeleteReportDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{tReport('delete.confirmTitle', '報告を削除')}</DialogTitle>
        <DialogContent>
          <Typography>
            {tReport('delete.confirmMessage', 'この報告を削除してもよろしいですか？')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteReportDialogOpen(false)}>
            {tReport('delete.cancel', 'キャンセル')}
          </Button>
          <Button onClick={handleDeleteReport} color="error" variant="contained">
            {tReport('delete.confirm', '削除')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 報告詳細ダイアログ */}
      <Dialog
        open={reportDetailDialogOpen}
        onClose={handleCloseReportDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{tReport('detail.title', '設問詳細')}</DialogTitle>
        <DialogContent dividers>
          {detailReport?.question && (
            <Box>
              {/* 設問情報 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {tReport('detail.questionText', '設問文')}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {detailReport.question.questionText}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Chip
                  label={`${tReport('detail.questionType', '設問タイプ')}: ${getQuestionTypeLabel(detailReport.question.questionType)}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`${tReport('detail.points', '配点')}: ${detailReport.question.points}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              </Box>

              {/* 選択肢（選択式の場合） */}
              {detailReport.question.options && detailReport.question.options.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {tReport('detail.options', '選択肢')}
                  </Typography>
                  <List dense>
                    {detailReport.question.options.map((option, index) => {
                      const isCorrect = isCorrectOption(
                        option,
                        detailReport.question!.correctAnswer,
                        detailReport.question!.questionType
                      )
                      return (
                        <ListItem key={index}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {isCorrect ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <RadioButtonUncheckedIcon color="disabled" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={option}
                            primaryTypographyProps={{
                              color: isCorrect ? 'success.main' : 'text.primary',
                              fontWeight: isCorrect ? 'bold' : 'normal',
                            }}
                          />
                        </ListItem>
                      )
                    })}
                  </List>
                </Box>
              )}

              {/* 正解（記述式の場合） */}
              {detailReport.question.questionType === 'SHORT_ANSWER' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {tReport('detail.correctAnswer', '正解')}
                  </Typography>
                  <Typography variant="body1" color="success.main" fontWeight="bold">
                    {detailReport.question.correctAnswer}
                  </Typography>
                </Box>
              )}

              {/* 解説 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {tReport('detail.explanation', '解説')}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {detailReport.question.explanation ||
                      tReport('detail.noExplanation', '解説なし')}
                  </Typography>
                </Paper>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* 報告情報 */}
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                {tReport('detail.reportInfo', '報告情報')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    {tReport('detail.reporter', '報告者')}
                  </Typography>
                  <Typography variant="body2">
                    {detailReport.reporter?.name || '-'}
                    {detailReport.reporter?.email && (
                      <Typography component="span" color="textSecondary" sx={{ ml: 1 }}>
                        ({detailReport.reporter.email})
                      </Typography>
                    )}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    {tReport('detail.reportedAt', '報告日時')}
                  </Typography>
                  <Typography variant="body2">{formatDateTime(detailReport.createdAt)}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    {tReport('detail.comment', '報告コメント')}
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.50' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {detailReport.comment || tReport('detail.noComment', 'コメントなし')}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReportDetail} variant="contained">
            {tReport('detail.close', '閉じる')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* テスト詳細モーダル */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              {t('detail.title')}
              {canEdit && selectedTest && selectedTest.questions?.length > 0 && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddQuestion}
                  variant="outlined"
                >
                  {t('question.add', '問題を追加')}
                </Button>
              )}
            </Box>
            <IconButton onClick={() => setDetailDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedTest && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedTest.title}
              </Typography>

              {selectedTest.description && (
                <Typography variant="body1" paragraph>
                  {selectedTest.description}
                </Typography>
              )}

              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('detail.basicInfo')}
                </Typography>
                <Typography variant="body2">
                  {t('detail.complianceRule')}: {selectedTest.complianceRule.title}
                </Typography>
                <Typography variant="body2">
                  {t('detail.category')}: {selectedTest.complianceRule.category}
                </Typography>
                <Typography variant="body2">
                  {t('detail.passingScore')}: {selectedTest.passingScore}%
                </Typography>
                {selectedTest.timeLimit && (
                  <Typography variant="body2">
                    {t('detail.timeLimit')}: {selectedTest.timeLimit}
                    {i18n.language === 'ja' ? '分' : ' min'}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('detail.questionList')} ({selectedTest.questions.length}
                  {i18n.language === 'ja' ? '問' : ''})
                </Typography>
                {selectedTest.questions.map((question: any, index: number) => (
                  <Card key={question.id} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t('detail.questionNumber', { number: index + 1 })}
                          </Typography>
                          <Typography variant="body2" paragraph>
                            {question.questionText}
                          </Typography>

                          <Typography variant="caption" color="textSecondary">
                            {t('detail.format')}: {t(`question.types.${question.questionType}`)}
                          </Typography>

                          {question.options && question.options.length > 0 && (
                            <Box mt={1}>
                              {question.options.map((option: string, optIndex: number) => (
                                <Typography key={optIndex} variant="body2" sx={{ ml: 2 }}>
                                  {optIndex + 1}. {option}
                                </Typography>
                              ))}
                            </Box>
                          )}

                          {/* 管理者向け: 正解・解説表示 */}
                          {canEdit && (
                            <Box
                              sx={{
                                mt: 2,
                                p: 1.5,
                                bgcolor: 'rgba(31, 58, 95, 0.1)',
                                borderRadius: 1,
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {t('question.form.correctAnswer', '正解')}:{' '}
                                {getCorrectAnswerDisplay(question)}
                              </Typography>
                              {question.explanation && (
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  {t('question.form.explanation', '解説')}: {question.explanation}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>

                        {/* 編集・削除・並び替えボタン */}
                        {canEdit && (
                          <Box display="flex" flexDirection="column" alignItems="center" ml={1}>
                            {selectedTest._count?.testResults > 0 && (
                              <Tooltip title={t('question.hasResultsWarning', '回答履歴あり')}>
                                <WarningIcon color="secondary" fontSize="small" sx={{ mb: 0.5 }} />
                              </Tooltip>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handleMoveUp(index)}
                              disabled={index === 0}
                            >
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveDown(index)}
                              disabled={index === selectedTest.questions.length - 1}
                            >
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleEditQuestion(question)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteQuestion(question)}
                              sx={{ color: 'text.secondary', '&:hover': { color: '#EF4444' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}

                {/* 設問がない場合 */}
                {selectedTest.questions.length === 0 && (
                  <Box textAlign="center" py={4}>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      {t('detail.noQuestions', '設問がありません')}
                    </Typography>
                    {canEdit && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddQuestion}
                      >
                        {t('question.add', '問題を追加')}
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* テスト作成ダイアログ（手動作成、現在未使用） */}
      <TestCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleTestCreated}
      />

      {/* AIテスト生成モーダル */}
      <TestGenerationModal
        open={aiGenerationOpen}
        onClose={() => setAiGenerationOpen(false)}
        onSuccess={() => {
          setSuccessMessage(t('create.aiSuccess', 'AIテスト生成を開始しました'))
          loadData()
        }}
      />

      {/* 設問編集ダイアログ */}
      <QuestionEditDialog
        open={questionDialogOpen}
        question={editingQuestion}
        onClose={() => {
          setQuestionDialogOpen(false)
          setEditingQuestion(null)
        }}
        onSave={handleSaveQuestion}
        hasTestResults={selectedTest?._count?.testResults > 0}
      />

      {/* 設問削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('question.delete', '問題を削除')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('question.deleteConfirm', 'この問題を削除してもよろしいですか？')}
          </Typography>
          {deletingQuestion && (
            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2">
                {deletingQuestion.questionText.substring(0, 100)}
                {deletingQuestion.questionText.length > 100 ? '...' : ''}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('form.cancel', 'キャンセル')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {t('form.delete', '削除')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* テスト削除確認ダイアログ */}
      <Dialog open={deleteTestDialogOpen} onClose={() => setDeleteTestDialogOpen(false)}>
        <DialogTitle>{t('delete.title', 'テストを削除')}</DialogTitle>
        <DialogContent>
          {deletingTest && deletingTest.resultCount > 0 ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t(
                  'delete.hasResultsMessage',
                  'このテストには受講履歴があります。削除すると無効化されます。'
                )}
              </Alert>
              <Typography>
                {t('delete.confirmDeactivate', 'このテストを無効化してもよろしいですか？')}
              </Typography>
            </>
          ) : (
            <Typography>{t('delete.confirm', 'このテストを削除してもよろしいですか？')}</Typography>
          )}
          {deletingTest && (
            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="subtitle2" gutterBottom>
                {deletingTest.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('list.questionCountLabel', { count: deletingTest.questionCount })}
              </Typography>
              {deletingTest.resultCount > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('list.resultCount', { count: deletingTest.resultCount })}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTestDialogOpen(false)}>
            {t('form.cancel', 'キャンセル')}
          </Button>
          <Button onClick={handleConfirmDeleteTest} color="error" variant="contained">
            {deletingTest && deletingTest.resultCount > 0
              ? t('delete.deactivateButton', '無効化')
              : t('form.delete', '削除')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 成功メッセージ */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default TestManagePage
